import { useState } from 'react';
import { X, Film, Mail, Lock, User as UserIcon, Loader2, AlertCircle, KeyRound, ShieldAlert } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { redeemInvitation } from '@/services/invitationService';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  invitationToken?: string;
  initialMode?: 'signin' | 'signup';
}

export function AuthModal({
  isOpen,
  onClose,
  onSuccess,
  invitationToken,
  initialMode = 'signin',
}: AuthModalProps) {
  const isInviteMode = Boolean(invitationToken);
  const [mode, setMode] = useState<'signin' | 'signup'>(isInviteMode ? initialMode : 'signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [redeemError, setRedeemError] = useState<string | null>(null);

  const {
    loading,
    authError,
    loginWithEmail,
    registerWithEmail,
    loginWithGoogle,
    refreshProfileStatus,
    clearAuthError,
  } = useAuth();

  if (!isOpen) return null;

  const handleTabSwitch = (newMode: 'signin' | 'signup') => {
    setMode(newMode);
    clearAuthError();
    setRedeemError(null);
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setRedeemError(null);
    if (!email.trim() || !password) return;

    if (mode === 'signin') {
      const res = await loginWithEmail(email, password);
      if (res) {
        if (isInviteMode && invitationToken) {
          const redeemRes = await redeemInvitation(invitationToken);
          if (!redeemRes.success) {
            setRedeemError(redeemRes.error || 'Could not redeem invitation.');
            return;
          }
        }
        onClose();
        onSuccess?.();
        refreshProfileStatus().catch(() => {});
      }
    } else {
      if (!isInviteMode || !invitationToken) {
        setRedeemError('Registration requires a valid invitation link.');
        return;
      }
      const res = await registerWithEmail(email, password, displayName);
      if (res) {
        const redeemRes = await redeemInvitation(invitationToken);
        if (!redeemRes.success) {
          setRedeemError(redeemRes.error || 'Account created, but invitation could not be redeemed.');
          return;
        }
        onClose();
        onSuccess?.();
        refreshProfileStatus().catch(() => {});
      }
    }
  };

  const handleGoogleSubmit = async () => {
    setRedeemError(null);
    try {
      const res = await loginWithGoogle();
      if (res) {
        if (isInviteMode && invitationToken) {
          const redeemRes = await redeemInvitation(invitationToken);
          if (!redeemRes.success) {
            setRedeemError(redeemRes.error || 'Signed in, but invitation could not be redeemed.');
            return;
          }
        }
        onClose();
        onSuccess?.();
        refreshProfileStatus().catch(() => {});
      }
    } catch (e: any) {
      setRedeemError(e.message || 'Google sign in failed');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in contain-overscroll">
      <div className="relative w-full max-w-md bg-cinema-900 border border-cinema-700/80 rounded-xl p-6 sm:p-7 shadow-cinema-screen text-cinema-100 contain-overscroll">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 rounded-lg text-cinema-400 hover:text-cinema-100 hover:bg-cinema-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp"
          aria-label="Close dialog"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-10 h-10 rounded-full bg-cinema-800 border border-cinema-700 flex items-center justify-center mx-auto text-lamp mb-3">
            {isInviteMode ? <KeyRound className="w-5 h-5 text-lamp" /> : <Film className="w-5 h-5" />}
          </div>
          <h2 className="text-lg font-bold text-cinema-100 tracking-tight">
            {isInviteMode
              ? mode === 'signup'
                ? 'Register with Invitation'
                : 'Sign In to Redeem Invite'
              : 'Sign in to Host'}
          </h2>
          <p className="text-xs text-cinema-400 mt-1 max-w-xs mx-auto">
            {isInviteMode
              ? 'Complete your registration to activate approved host privileges.'
              : 'WatchParty is private & invite-only. Only approved hosts can stream.'}
          </p>
        </div>

        {/* Tab Selector (Only shown if invitation is present) */}
        {isInviteMode ? (
          <div className="flex hairline-b bg-cinema-950/80 p-1 rounded-lg border border-cinema-700/60 mb-5">
            <button
              type="button"
              onClick={() => handleTabSwitch('signup')}
              className={`flex-1 py-1.5 rounded text-xs font-semibold transition-colors ${
                mode === 'signup'
                  ? 'bg-cinema-800 text-cinema-100 shadow-sm'
                  : 'text-cinema-400 hover:text-cinema-200'
              }`}
            >
              Create Account
            </button>
            <button
              type="button"
              onClick={() => handleTabSwitch('signin')}
              className={`flex-1 py-1.5 rounded text-xs font-semibold transition-colors ${
                mode === 'signin'
                  ? 'bg-cinema-800 text-cinema-100 shadow-sm'
                  : 'text-cinema-400 hover:text-cinema-200'
              }`}
            >
              Existing Account
            </button>
          </div>
        ) : (
          /* Public Mode Notice */
          <div className="mb-5 p-3 rounded-lg bg-cinema-950/90 border border-cinema-750 text-xs text-cinema-300 flex items-start gap-2.5">
            <ShieldAlert className="w-4 h-4 text-lamp flex-shrink-0 mt-0.5" />
            <div className="leading-relaxed text-[11px]">
              <span className="font-semibold text-cinema-100">Private Screening Community: </span>
              Account registration requires a personal invitation from the administrator. If you already have an account, sign in below.
            </div>
          </div>
        )}

        {/* Error Alert */}
        {(authError || redeemError) && (
          <div className="mb-4 p-3 rounded-lg bg-red-950/50 border border-red-500/30 text-red-300 text-xs flex items-center gap-2 animate-fade-in">
            <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
            <span className="leading-tight">{redeemError || authError}</span>
          </div>
        )}

        {/* One-Click Google Auth */}
        <button
          type="button"
          onClick={handleGoogleSubmit}
          disabled={loading}
          className="w-full py-2.5 px-4 rounded-lg bg-cinema-950 hover:bg-cinema-850 border border-cinema-700 text-xs font-semibold text-cinema-100 transition-all flex items-center justify-center gap-2.5 shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp disabled:opacity-50"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.8-2.4 3.68v3.05h3.88c2.27-2.09 3.66-5.17 3.66-9.17z"
            />
            <path
              fill="#34A853"
              d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.34 24 12 24z"
            />
            <path
              fill="#FBBC05"
              d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.13-1.55.38-2.27V6.58H1.25C.45 8.18 0 9.98 0 12s.45 3.82 1.25 5.42l4.03-3.15z"
            />
            <path
              fill="#EA4335"
              d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.34 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z"
            />
          </svg>
          <span>{isInviteMode && mode === 'signup' ? 'Register with Google' : 'Continue with Google'}</span>
        </button>

        {/* Divider */}
        <div className="relative my-4 flex items-center justify-center">
          <div className="w-full border-t border-cinema-800" />
          <span className="absolute px-3 bg-cinema-900 text-[10px] uppercase font-mono tracking-wider text-cinema-500">
            or with email
          </span>
        </div>

        {/* Email & Password Form */}
        <form onSubmit={handleEmailSubmit} className="space-y-3.5">
          {mode === 'signup' && (
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-cinema-300 mb-1">
                Display Name
              </label>
              <div className="relative">
                <UserIcon className="w-4 h-4 text-cinema-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="e.g. Alex"
                  className="w-full bg-cinema-950 border border-cinema-700/80 rounded-lg pl-9 pr-3 py-2 text-xs text-cinema-100 placeholder-cinema-600 focus:outline-none focus:border-lamp"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-cinema-300 mb-1">
              Email Address
            </label>
            <div className="relative">
              <Mail className="w-4 h-4 text-cinema-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
                className="w-full bg-cinema-950 border border-cinema-700/80 rounded-lg pl-9 pr-3 py-2 text-xs text-cinema-100 placeholder-cinema-600 focus:outline-none focus:border-lamp"
              />
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-cinema-300 mb-1">
              Password
            </label>
            <div className="relative">
              <Lock className="w-4 h-4 text-cinema-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                minLength={6}
                className="w-full bg-cinema-950 border border-cinema-700/80 rounded-lg pl-9 pr-3 py-2 text-xs text-cinema-100 placeholder-cinema-600 focus:outline-none focus:border-lamp"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 px-4 rounded-lg bg-lamp hover:bg-lamp-bright text-cinema-950 font-bold text-xs tracking-wide transition-all shadow-md flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp disabled:opacity-50 mt-2"
          >
            {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            <span>
              {mode === 'signin' ? 'Sign In as Host' : 'Create Host Account'}
            </span>
          </button>
        </form>

        {/* Invite link notice for account creation */}
        {!isInviteMode && (
          <div className="mt-4 pt-3 hairline-t text-center text-[11px] text-cinema-400">
            <span>Need an account? </span>
            <span className="text-cinema-300">
              Account creation is invite-only. Ask the host or administrator for an invitation link to create an account.
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

